import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styles from "./AuthPage.module.css";
import { authService } from "../../services/authService";
import api from "../../services/api";
import { useFacebookSDK } from "../../hooks/useFacebookSDK";
import { useAuth } from "../../auth/AuthContext";
import { clearSession } from "../../auth/session";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import SeoHead from "../../components/Seo/SeoHead";
import {
  clearCtaContext,
  readCtaContext,
  trackGenerateLead,
  trackSignUp,
} from "../../analytics";
import {
  TRIAL_PRODUCT_PARAM,
  isTrialProduct,
} from "../../constants/trialProduct";

const DEMO_MESSAGES = [
  { type: "out", text: "Oi, queria marcar um corte" },
  { type: "in", text: "Olá! Qual barbeiro você prefere?", buttons: ["João", "Pedro", "Tanto faz"] },
  { type: "out", text: "João" },
  { type: "in", text: "João tem horário hoje 16h, amanhã 10h ou sexta 14h. Qual fica melhor?" },
  { type: "out", text: "Hoje 16h" },
  { type: "in", text: "✅ Confirmado! Corte com João hoje às 16h. Te lembro 2h antes." },
];

const humanizeError = (msg = "") => {
  if (
    msg.includes("credentials") ||
    msg.includes("Invalid") ||
    msg.includes("senha") ||
    msg.includes("password")
  )
    return "Não encontramos uma conta com esse e-mail e senha. Quer recuperar a senha?";
  if (msg.includes("google") || msg.includes("OAuth"))
    return "Esta conta foi criada com Google. Use o botão Continuar com Google acima.";
  if (
    msg.includes("already") ||
    msg.includes("já cadastrado") ||
    msg.includes("exists")
  )
    return "Esse e-mail já tem cadastro. Quer entrar?";
  if (
    msg.includes("network") ||
    msg.includes("fetch") ||
    msg.includes("ECONNREFUSED")
  )
    return "Não conseguimos nos conectar. Verifique sua internet e tente de novo.";
  return msg || "Algo deu errado do nosso lado. Tente novamente em alguns segundos.";
};

/**
 * Fecha o funil de aquisição no GA4: `sign_up` + `generate_lead`, atribuídos ao
 * CTA que trouxe o visitante (ou marcados como entrada direta).
 *
 * Blindado em try/catch: nenhuma falha de medição ou de storage pode impedir o
 * salvamento da autenticação, o redirect ou o onboarding.
 */
function trackSignUpAndLead() {
  try {
    const ctx = readCtaContext();
    trackSignUp();
    trackGenerateLead(ctx);
    clearCtaContext();
  } catch (_err) {
    // medição nunca bloqueia o cadastro
  }
}

// Lê e consome a assinatura pendente do localStorage de forma resiliente.
// Nunca lança: storage bloqueado (modo privado/política/quota) ou valor
// corrompido degradam para "não há assinatura pendente", preservando o
// redirect e o onboarding. A chave é removida ANTES do parse — um valor
// corrompido que sobrevivesse quebraria todo login seguinte.
const consumePendingSubscription = () => {
  let raw = null;

  try {
    raw = localStorage.getItem("pendingSubscription");
  } catch (_err) {
    return null;
  }

  if (!raw) return null;

  try {
    localStorage.removeItem("pendingSubscription");
  } catch (_err) {
    // storage indisponível para escrita — segue com o valor já lido
  }

  try {
    const parsed = JSON.parse(raw);
    const isPlainObject =
      typeof parsed === "object" && parsed !== null && !Array.isArray(parsed);
    return isPlainObject ? parsed : null;
  } catch (_err) {
    return null;
  }
};

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshUser } = useAuth();

  // ✅ Estados de formulário
  const [mode, setMode] = useState(() =>
    searchParams.get("tab") === "signup" ? "signup" : "login"
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ✅ Estados de validação
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
  });

  // ✅ Estados de requisição
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // ✅ Verificar se já está logado ao montar componente
  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");

      const hasValidToken =
        token && typeof token === "string" && token.length > 0;
      const hasValidUser = userStr && typeof userStr === "string";

      if (hasValidToken && hasValidUser) {
        try {
          JSON.parse(userStr);

          // Assinatura pendente é lida pelo mesmo helper resiliente do
          // handlePostAuth: valor corrompido vira "sem assinatura pendente" em
          // vez de cair no catch abaixo, que derrubaria a sessão do usuário.
          const pendingSub = consumePendingSubscription();
          if (pendingSub) {
            if (pendingSub.isFree) {
              navigate("/visao-geral", { replace: true });
              return;
            }

            setLoading(true);
            setSuccessMessage("Verificando sua assinatura...");

            api
              .get("/users/check-payment-profile")
              .then((profileRes) => {
                let doc = null;
                if (!profileRes.data.hasDoc) {
                  doc = window.prompt(
                    "Para gerar sua assinatura, o sistema de pagamentos exige o seu CPF/CNPJ (apenas uma vez):"
                  );
                  if (!doc) {
                    setLoading(false);
                    navigate("/visao-geral", { replace: true });
                    return;
                  }
                }

                return api.post("/payments/create-subscription", {
                  planId: pendingSub.planId,
                  cpfCnpj: doc,
                });
              })
              .then((subResponse) => {
                if (subResponse && subResponse.data.invoiceUrl) {
                  window.location.href = subResponse.data.invoiceUrl;
                } else {
                  navigate("/visao-geral", { replace: true });
                }
              })
              .catch((_error) => {
                setError(
                  "Erro ao processar pagamento. Redirecionando ao dashboard..."
                );
                setTimeout(() => {
                  navigate("/visao-geral", { replace: true });
                }, 3000);
              });

            return;
          }

          navigate("/visao-geral", { replace: true });
        } catch (_err) {
          // `user` no storage está corrompido: a sessão é inutilizável.
          // INV-F2 — derruba a sessão INTEIRA, não só token+user.
          clearSession();
        }
      }
    } catch (_err) {
      // silencioso — dados inválidos, ignora
    }
  }, [navigate]);

  useFacebookSDK(process.env.REACT_APP_META_APP_ID, {
    cookie: true,
    xfbml: true,
  });

  // ✅ Limpar mensagens de sucesso/erro após 5 segundos
  useEffect(() => {
    if (error || successMessage) {
      const timer = setTimeout(() => {
        setError("");
        setSuccessMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, successMessage]);

  // ✅ Força de senha
  const passwordStrength = useMemo(() => {
    if (password.length < 8) return "weak";
    if (/[a-zA-Z]/.test(password) && /[0-9]/.test(password)) return "strong";
    return "medium";
  }, [password]);

  const strengthLabel = {
    weak: "Senha fraca",
    medium: "Senha média",
    strong: "Senha forte",
  }[passwordStrength];

  // ✅ Validação com useMemo
  const errors = useMemo(() => {
    const e = {};
    if (mode === "signup" && (!name || name.trim().length < 2)) {
      e.name = "Informe seu nome (mín. 2 caracteres).";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (!emailRegex.test(email)) {
      e.email = "E-mail inválido.";
    }
    if (!password || password.length < 6) {
      e.password = "Senha deve ter pelo menos 6 caracteres.";
    }
    return e;
  }, [mode, name, email, password]);

  const isValid = Object.keys(errors).length === 0;

  // ✅ Função para enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValid) {
      setError("Por favor, preencha todos os campos corretamente.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      let response;

      if (mode === "login") {
        response = await authService.login(email, password);

        if (response.success) {
          await handlePostAuth(
            response,
            "Login realizado com sucesso! Redirecionando..."
          );
        } else {
          throw new Error(response.message || "Erro ao fazer login");
        }
      } else {
        // O produto escolhido no site (modal da home, ou `/agendamento` /
        // `/atendimento`) chega aqui pela URL e decide o plano de trial no
        // backend. Query param, e não storage: sobrevive a link direto, é
        // compartilhável, e não recria a categoria de chave morta que acabou de
        // sair do `localStorage`.
        response = await authService.register(
          name,
          email,
          password,
          searchParams.get(TRIAL_PRODUCT_PARAM),
        );

        if (response.success) {
          // Lead = conta criada com sucesso. Fica SÓ neste ramo: `handlePostAuth`
          // também roda no login, e medir lá faria login virar lead.
          trackSignUpAndLead();
          // NÃO gravar `tenantId` solto no localStorage: ninguém o lê. O tenant
          // do usuário logado vem de `localStorage.user` (gravado por
          // `handlePostAuth` → `authService`). Ver `src/auth/session.js`.
          await handlePostAuth(response, "Conta criada! Redirecionando...");
        } else {
          throw new Error(response.message || "Erro ao criar conta");
        }
      }
    } catch (err) {
      setError(humanizeError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePostAuth = async (response, defaultMessage) => {
    const pendingSub = consumePendingSubscription();
    if (pendingSub) {
      if (pendingSub.isFree) {
        setSuccessMessage(defaultMessage);
        await refreshUser();
        setTimeout(() => {
          navigate("/agenda?setup=true", { replace: true });
        }, 1500);
        return;
      }

      setSuccessMessage("Autenticado! Iniciando pagamento...");
      try {
        const profileRes = await api.get("/users/check-payment-profile");
        let doc = null;

        if (!profileRes.data.hasDoc) {
          doc = window.prompt(
            "Para gerar sua assinatura, o sistema de pagamentos exige o seu CPF/CNPJ (apenas uma vez):"
          );
          if (!doc) {
            setLoading(false);
            navigate("/visao-geral", { replace: true });
            return;
          }
        }

        const subResponse = await api.post("/payments/create-subscription", {
          planId: pendingSub.planId,
          cpfCnpj: doc,
        });

        if (subResponse.data.invoiceUrl) {
          window.location.href = subResponse.data.invoiceUrl;
          return;
        }
      } catch (error) {
        setError("Erro ao processar pagamento. Redirecionando ao dashboard...");
        await refreshUser();
        setTimeout(() => {
          navigate("/visao-geral", { replace: true });
        }, 3000);
        return;
      }
    }

    setSuccessMessage(defaultMessage);
    await refreshUser();
    setTimeout(() => {
      navigate(mode === "signup" ? "/agenda?setup=true" : "/visao-geral", {
        replace: true,
      });
    }, 1500);
  };

  // ✅ Alterar modo (login/registro) + sync URL
  const handleModeChange = (newMode) => {
    setSearchParams(newMode === "signup" ? { tab: "signup" } : {}, {
      replace: true,
    });
    setMode(newMode);
    setError("");
    setSuccessMessage("");
    setName("");
    setEmail("");
    setPassword("");
    setTermsAccepted(false);
    setTouched({ name: false, email: false, password: false });
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    // O produto escolhido no site precisa ATRAVESSAR o OAuth. Sem ele aqui, a
    // cadeia inteira vira código morto: o `captureProdutoInSession` do backend
    // não grava, o callback não põe nada no fragmento, o `rememberTrialProduct`
    // guarda `null` e o `POST /tenants/setup` recebe `produto: undefined`.
    // O visitante que leu `/atendimento` e clicou em "Continuar com Google"
    // nasceria em FREE (agendamento) com status 201 e toast de sucesso,
    // descobrindo o erro só no primeiro 403 de feature.
    //
    // Produto inválido → SEM query string, nunca um default inventado. Mesma
    // regra que `buildTrialSignupPath` documenta: ausência é informação ("não
    // escolheu"), e um default apagaria essa diferença — inclusive no GA4.
    const produto = searchParams.get(TRIAL_PRODUCT_PARAM);
    const query = isTrialProduct(produto)
      ? `?${TRIAL_PRODUCT_PARAM}=${encodeURIComponent(produto)}`
      : "";
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}api/auth/google${query}`;
  };

  return (
    <div className={styles.loginPage}>
      <SeoHead
        title="Entrar | Nexus Chatbot"
        description="Acesse sua conta da Nexus Chatbot."
        canonical="https://nexustech.tec.br/login"
        noindex
      />

      {/* Coluna esquerda: form */}
      <div className={styles.formCol}>
        <div className={styles.formInner}>

          {/* Tabs */}
          <div
            className={styles.tabs}
            role="tablist"
            aria-label="Alternar entre entrar e criar conta"
          >
            <button
              role="tab"
              aria-selected={mode === "login"}
              className={styles.tab}
              onClick={() => handleModeChange("login")}
              disabled={loading}
            >
              Entrar
            </button>
            <button
              role="tab"
              aria-selected={mode === "signup"}
              className={styles.tab}
              onClick={() => handleModeChange("signup")}
              disabled={loading}
            >
              Criar conta
            </button>
          </div>

          {/* H1 contextual */}
          <h1 id="authTitle" className={styles.headline}>
            {mode === "login" ? "Bem-vindo de volta" : "Comece grátis em 30 segundos"}
          </h1>
          {mode === "signup" && (
            <p className={styles.subheadline}>
              Sem cartão de crédito. Cancele quando quiser.
            </p>
          )}

          {/* Alertas */}
          {error && (
            <div
              className={`${styles.alert} ${styles.alertError}`}
              role="alert"
              aria-live="polite"
            >
              {error}
            </div>
          )}
          {successMessage && (
            <div
              className={`${styles.alert} ${styles.alertSuccess}`}
              role="alert"
              aria-live="polite"
            >
              {successMessage}
            </div>
          )}

          {/* Form */}
          <form className={styles.form} noValidate onSubmit={handleSubmit}>

            {/* Botão Google */}
            <button
              type="button"
              className={styles.googleBtn}
              onClick={handleGoogleLogin}
              aria-label="Continuar com Google"
              disabled={loading}
            >
              <span className={styles.googleG} aria-hidden="true">G</span>
              <span>Continuar com Google</span>
            </button>

            {/* Divisor */}
            <div className={styles.divider} role="separator" aria-label="ou">
              ou
            </div>

            {/* Campo Nome (apenas signup) */}
            {mode === "signup" && (
              <div className={styles.field}>
                <label htmlFor="name" className={styles.label}>
                  Seu nome
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  className={`${styles.input} ${
                    touched.name && errors.name ? styles.inputError : ""
                  }`}
                  placeholder="Ex.: Maria Silva"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  aria-invalid={touched.name && !!errors.name}
                  aria-describedby={
                    touched.name && errors.name ? "name-error" : undefined
                  }
                  disabled={loading}
                />
                {touched.name && errors.name && (
                  <span id="name-error" className={styles.errorText}>
                    {errors.name}
                  </span>
                )}
              </div>
            )}

            {/* Campo E-mail */}
            <div className={styles.field}>
              <label htmlFor="email" className={styles.label}>
                Seu melhor e-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className={`${styles.input} ${
                  touched.email && errors.email ? styles.inputError : ""
                }`}
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                aria-invalid={touched.email && !!errors.email}
                aria-describedby={
                  touched.email && errors.email ? "email-error" : undefined
                }
                autoComplete="email"
                autoFocus
                disabled={loading}
              />
              {touched.email && errors.email && (
                <span id="email-error" className={styles.errorText}>
                  {errors.email}
                </span>
              )}
            </div>

            {/* Campo Senha */}
            <div className={styles.field}>
              <label htmlFor="password" className={styles.label}>
                {mode === "login" ? "Sua senha" : "Crie uma senha"}
              </label>
              <div className={styles.inputWrap}>
                <input
                  id="password"
                  name="password"
                  type={showPass ? "text" : "password"}
                  className={`${styles.input} ${
                    touched.password && errors.password
                      ? styles.inputError
                      : ""
                  }`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, password: true }))
                  }
                  aria-invalid={touched.password && !!errors.password}
                  aria-describedby={
                    touched.password && errors.password
                      ? "password-error"
                      : undefined
                  }
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                  disabled={loading}
                />
                <button
                  type="button"
                  className={styles.showBtn}
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}
                  disabled={loading}
                >
                  {showPass ? "Ocultar" : "Mostrar"}
                </button>
              </div>
              {touched.password && errors.password && (
                <span id="password-error" className={styles.errorText}>
                  {errors.password}
                </span>
              )}

              {/* Indicador de força de senha (apenas signup) */}
              {mode === "signup" && password.length > 0 && (
                <div className={styles.strengthWrap}>
                  <div className={styles.strengthBar}>
                    <div
                      className={styles.strengthFill}
                      data-strength={passwordStrength}
                    />
                  </div>
                  <span className={styles.strengthLabel}>{strengthLabel}</span>
                </div>
              )}
            </div>

            {/* Link "Esqueci minha senha" (apenas login) */}
            {mode === "login" && (
              <Link to="/forgot-password" className={styles.forgotLink}>
                Esqueci minha senha
              </Link>
            )}

            {/* Botão submit */}
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={!isValid || loading || (mode === "signup" && !termsAccepted)}
              aria-disabled={!isValid || loading || (mode === "signup" && !termsAccepted)}
            >
              {loading ? (
                <>
                  <span className={styles.spinner} aria-hidden="true" />
                  {mode === "login" ? "Entrando..." : "Criando conta..."}
                </>
              ) : mode === "login" ? (
                "Entrar"
              ) : (
                "Criar conta grátis"
              )}
            </button>

            {/* Checkbox de termos (apenas signup) */}
            {mode === "signup" && (
              <div className={styles.termsRow}>
                <label className={styles.termsLabel}>
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className={styles.termsCheckbox}
                  />
                  Aceito os{" "}
                  <Link
                    to="/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.termsLink}
                  >
                    Termos de Uso
                  </Link>{" "}
                  e{" "}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.termsLink}
                  >
                    Política de Privacidade
                  </Link>
                </label>
              </div>
            )}

            {/* Footer de troca de tab */}
            <p className={styles.switchTab}>
              {mode === "login" ? (
                <>
                  Não tem conta?{" "}
                  <button
                    type="button"
                    className={styles.switchLink}
                    onClick={() => handleModeChange("signup")}
                  >
                    Criar agora →
                  </button>
                </>
              ) : (
                <>
                  Já tem conta?{" "}
                  <button
                    type="button"
                    className={styles.switchLink}
                    onClick={() => handleModeChange("login")}
                  >
                    Entrar →
                  </button>
                </>
              )}
            </p>

            {/* Link de ajuda WhatsApp */}
            <p className={styles.helpLink}>
              Precisa de ajuda?{" "}
              <a
                href="https://wa.me/5551992747402?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20o%20cadastro%2Flogin%20na%20Nexus%20Tech"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.helpAnchor}
              >
                Fale com a gente no WhatsApp
              </a>{" "}
              51 99274-7402
            </p>
          </form>
        </div>
      </div>

      {/* Coluna direita: painel azul */}
      <div className={styles.panel} aria-hidden="true">
        <WhatsAppMockup messages={DEMO_MESSAGES} looping={true} />
        <ul className={styles.bullets}>
          <li>
            <span className={styles.check}>✓</span> Configure em 15 minutos
          </li>
          <li>
            <span className={styles.check}>✓</span> Sem cartão de crédito
          </li>
          <li>
            <span className={styles.check}>✓</span> Cancele quando quiser
          </li>
        </ul>
      </div>
    </div>
  );
}
